/**
 * Account membership — a person's authority over an ORGANIZATION (an account), independent of any
 * workspace membership. Plan-014 unit A2; design §27.3.3, §27.5.2; ADR 0033; FR12-AUTH-01.
 *
 * Console copy says **Organization**; storage, API and code say **account** (plan-014 ED-14).
 *
 * THE TWO MEMBERSHIPS ARE INDEPENDENT. A person may hold an `account_membership` and no workspace
 * membership (Finance, who manages the money and sees no conversation), or workspace memberships and
 * no account membership (a Member of one workspace), or both. Neither implies the other, and neither
 * is a fallback for the other: an account role grants account capabilities and nothing operational
 * inside a workspace (fixture `operational_content_requires_workspace_role`).
 *
 * What this file holds is PURE: the roles, the row, the owner-protection rule, and the grouping of
 * `account_access_for_person()`'s rows into the organization picker's choices. The SQL lives in
 * `packages/db/src/tenant/account-membership.ts` (writes, tenant role) and
 * `packages/db/src/identity/account-access.ts` (the definer read, identity role) — DD14-1.
 */
import { idCodec } from "../id/brand.js";
import { ACCOUNT_STATE, MEMBER_ROLE } from "./account.js";
/** The codec: the only way a string from a wire or a row becomes an {@link AccountMembershipId}. */
export const accountMembershipId = idCodec("AccountMembershipId");
/**
 * The four account roles of plan-012 §2.2 (design §27.5.2), in `account_role`'s order — the order
 * the fixture `packages/domain/src/identity/fixture/capability-matrix.json` lists them in.
 */
export const ACCOUNT_ROLE = ["owner", "admin", "finance", "viewer"];
const ACCOUNT_ROLE_SET = new Set(ACCOUNT_ROLE);
/** Whether a value read from a row or a wire is one of the four account roles. */
export function isAccountRole(value) {
    return typeof value === "string" && ACCOUNT_ROLE_SET.has(value);
}
/** `account_membership.state`. Removal is a state, never a DELETE. */
export const ACCOUNT_MEMBERSHIP_STATE = ["active", "removed"];
/** Why the membership exists — `account_membership.provenance`'s closed set. */
export const ACCOUNT_MEMBERSHIP_PROVENANCE = [
    "creation",
    "invitation",
    "ownership_transfer",
    "operator_request",
    "migration",
];
/**
 * Whether `change` to a membership currently holding `currentRole` would leave the account with no
 * active Owner, given how many active Owners it has NOW (counted under a lock — the caller's job).
 *
 * `last_owner_protected` is the refusal; `null` means the rule does not stand in the way. It judges
 * only the last-Owner invariant: who may make the change (an Admin acting on an Owner, self-promotion)
 * is the capability evaluator's (A3) and the team routes' (B2).
 */
export function lastOwnerRefusalOf(currentRole, change, activeOwnerCount) {
    if (!Number.isInteger(activeOwnerCount) || activeOwnerCount < 0) {
        throw new RangeError(`activeOwnerCount must be a whole number, received ${String(activeOwnerCount)}`);
    }
    if (currentRole !== "owner")
        return null;
    const staysOwner = change.kind === "role_change" && change.toRole === "owner";
    if (staysOwner)
        return null;
    return activeOwnerCount <= 1 ? "last_owner_protected" : null;
}
/**
 * Groups `account_access_for_person()`'s rows into one choice per organization, in the order the
 * function returned them (by organization name, then workspace name). An organization appears once
 * whether the person reaches it through an account role, workspaces, or both.
 */
export function accountChoiceOf(entries) {
    const seen = [];
    const byAccount = new Map();
    for (const entry of entries) {
        let held = byAccount.get(entry.accountId);
        if (held === undefined) {
            held = { accountName: entry.accountName, accountState: entry.accountState, accountRole: null, workspace: [] };
            byAccount.set(entry.accountId, held);
            seen.push(entry.accountId);
        }
        if (entry.kind === "account_membership") {
            held.accountRole = entry.accountRole;
        }
        else {
            held.workspace.push({
                workspaceId: entry.workspaceId,
                workspaceName: entry.workspaceName,
                workspaceState: entry.workspaceState,
                memberRole: entry.memberRole,
            });
        }
    }
    return seen.map((accountId) => {
        const held = byAccount.get(accountId);
        if (held === undefined)
            throw new Error("accountChoiceOf: an organization was ordered but never grouped");
        return {
            accountId,
            accountName: held.accountName,
            accountState: held.accountState,
            accountRole: held.accountRole,
            workspace: held.workspace,
        };
    });
}
const ACCOUNT_STATE_SET = new Set(ACCOUNT_STATE);
const MEMBER_ROLE_SET = new Set(MEMBER_ROLE);
/** Whether a value read from a row is one of `account_state`'s values. */
export function isAccountState(value) {
    return typeof value === "string" && ACCOUNT_STATE_SET.has(value);
}
/**
 * Whether a value read from a row is a member role THIS release knows. A workspace preset added by
 * A3's migration 0079 (`developer`, `campaign_manager`, `billing`) is not one until A3 widens
 * {@link MemberRole}; a reader refuses the row rather than guessing a role for it.
 */
export function isKnownMemberRole(value) {
    return typeof value === "string" && MEMBER_ROLE_SET.has(value);
}
//# sourceMappingURL=account-membership.js.map